import { useCallback, useRef, useState } from 'react';

import { Updater, VisibilityState } from '@tanstack/react-table';

import { getFromLocalStorage, setToLocalStorage } from '@/utils/localStorage';

const COLUMN_VISIBILITY_STORAGE_PREFIX = 'table-column-visibility';

/** Builds the localStorage key for a table's column visibility, or null when persistence is off. */
const buildStorageKey = (tableId: string | undefined): string | null =>
  tableId ? `${COLUMN_VISIBILITY_STORAGE_PREFIX}-${tableId}` : null;

interface UseColumnVisibilityOptions {
  /** Fallback visibility used when nothing is persisted yet (or persistence is off). */
  initialVisibility?: VisibilityState;
}

interface UseColumnVisibilityResult {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (updaterOrValue: Updater<VisibilityState>) => void;
  /** Persists the current visibility on demand (a no-op when `tableId` is undefined). */
  saveColumnVisibility: () => void;
}

/**
 * Manages column visibility state with localStorage persistence, auto-saving on every change.
 *
 * The single source of truth for column-visibility persistence, consumed both by `useDataTable`
 * (the standard table path) and directly by tables that cannot use `useDataTable` (e.g. the
 * approvals nested-pagination table, which owns its own TanStack instance). The storage key —
 * `table-column-visibility-{tableId}` — is shared, so a table can move onto/off `useDataTable`
 * later without resetting a user's saved preference.
 *
 * Pass `tableId` = undefined to disable persistence (state stays in memory only).
 */
export function useColumnVisibility(
  tableId: string | undefined,
  { initialVisibility }: UseColumnVisibilityOptions = {}
): UseColumnVisibilityResult {
  const storageKey = buildStorageKey(tableId);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    if (!storageKey) return initialVisibility ?? {};
    return getFromLocalStorage<VisibilityState>(storageKey) ?? initialVisibility ?? {};
  });

  const columnVisibilityRef = useRef(columnVisibility);
  columnVisibilityRef.current = columnVisibility;

  const onColumnVisibilityChange = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      const newVisibility =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnVisibilityRef.current)
          : updaterOrValue;

      setColumnVisibility(newVisibility);
      columnVisibilityRef.current = newVisibility;
      if (storageKey) setToLocalStorage(storageKey, newVisibility);
    },
    [storageKey]
  );

  const saveColumnVisibility = useCallback(() => {
    if (storageKey) setToLocalStorage(storageKey, columnVisibilityRef.current);
  }, [storageKey]);

  return { columnVisibility, onColumnVisibilityChange, saveColumnVisibility };
}
